import { useEffect } from 'react';
import { matchPath, useHistory, useLocation, useParams } from 'react-router';
import { Location } from 'history';
import { useFormTokenChainContext } from '../../../hooks/context/useFormTokenChainContext';
import useRoutes from '../../../hooks/useRoutes';
import { useSelectedRowsContext } from '../../../hooks/context/useSelectedRowsContext';
import { getSubsetAndItemParams } from '../../../utils/getSubsetAndItemParams';
import { getDatasetUrlParams } from '../../../utils/getDatasetUrlParams';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { GlobalLocationStateType } from '../../../types/types';

// react-router v5 / history v4 supports only ONE history.block at a time;
// a second call silently replaces the first. Both navigation flows for the
// form page (back button + external-URL same-dataset menu click) share this
// single blocker — split into per-flow handlers below for clarity.
export const useFormNavigationGuard = () => {
  const history = useHistory();
  const location = useLocation<GlobalLocationStateType>();
  const routes = useRoutes();
  const { datasetId, subsetItemId } = useParams<{
    datasetId: string;
    subsetItemId?: string;
  }>();
  const { getActualTokenFromUrl, popToken } = useFormTokenChainContext();
  const { resetSelectedRows, setExpandedRow } = useSelectedRowsContext();
  const { data, dataset } = useDatasetContext();

  useEffect(() => {
    // External-URL form → menu click on same dataset's list.
    // Mutates target.state so DatasetList refetches fresh data on mount.
    const handleExternalUrlPushToSameDataset = (target: Location): void => {
      const token = getActualTokenFromUrl();
      if (!token?.meta?.enteredFromExternalUrl) return;

      const match = matchPath(target.pathname, {
        path: routes.relative.datasetList(),
        exact: true,
        strict: false,
      });
      if (!match) return;

      const { datasetId: targetDatasetId } = getDatasetUrlParams(
        target.pathname
      );
      if (datasetId !== targetDatasetId) return;

      target.state = {
        ...((target.state as Record<string, unknown>) ?? {}),
        triggerInitData: true,
      };
    };

    // Form back button (POP) → redirect to token.fromUrl with cleanup state
    // (forceFindInDataset, subset flags, filterFirst flags as applicable).
    // Returns false to cancel the original POP; performs replace asynchronously.
    const handleFormBackPop = (
      target: Location,
      unblock: () => void
    ): false | undefined => {
      const token = getActualTokenFromUrl();
      if (!token) return undefined;

      let url = token.fromUrl ?? routes.datasetList(datasetId);
      const sanitizedTagetState = (
        typeof target.state === 'object' ? target.state : {}
      ) as GlobalLocationStateType;
      let routerState: GlobalLocationStateType = {
        ...sanitizedTagetState,
      };

      if (!token.parentTokenId) {
        // Keep /detail/ — useFormReturnDetailFetchGuard probes the row.
        resetSelectedRows();
        setExpandedRow(null);
        const { itemId } = getSubsetAndItemParams(url);

        if (itemId) {
          routerState = { ...routerState, forceFindInDataset: true };
          // Form entered from external URL: force a fresh fetch on return,
          // mirroring the save/cancel path in closeDatasetForm. Otherwise
          // DatasetList would search stale current data and display old rows.
          if (token.meta?.enteredFromExternalUrl) {
            routerState = { ...routerState, forceNewData: true };
          }
        }

        // Subset edit/copy form -> tell slave grid to relocate the row
        if (subsetItemId) {
          routerState = {
            ...routerState,
            subsetItemId,
            subsetFromForms: true,
          };
        }

        // Dataset-bound forms only — a standalone (menu) form returns via
        // plain navigation and the context dataset is a stale leftover there
        if (datasetId && dataset?.filterFirst) {
          routerState = {
            ...routerState,
            fetchDataOnFilterFirstDataset: !data ? true : undefined,
            omitFilterFirstFilter: true,
          };
        }
      }

      popToken(token.id);
      // push (not replace) — Ionic does not clear the forward stack on a
      // cross-IonPage replace (form -> /detail/), push always does.
      window.setTimeout(() => {
        unblock();
        history.push(url, routerState);
      }, 100);

      return false;
    };

    const unblock = history.block((target, action) => {
      if (action === 'PUSH') {
        handleExternalUrlPushToSameDataset(target);
        return;
      }
      if (action === 'POP') {
        return handleFormBackPop(target, unblock);
      }
    });

    return unblock;
  }, [history, getActualTokenFromUrl, popToken, routes, datasetId, location]);
};
